// Runtime interface host for a transaction embedded as a realtime LIST.
//
// The saved embed renders the standard saved-records table (EmbedLivePreview
// → ListView). This host wraps that and adds a runtime-only "View" dropdown
// in the search/filter toolbar row so the *viewer* can swap, without touching
// the saved config, to one of the other interfaces the embed system already
// implements for the same transaction:
//
//   list        — saved-records table (default)            → EmbedLivePreview
//   tabular     — multi-row inline entry grid, save in one → EmbedCaptureTabularPreview
//   form-input  — list with a popup add/edit form          → EmbedCapturePreview
//
// All three share GridToolbar (or, for form-input, an equivalent toolbar row),
// so the same <InterfaceSwitcher> node is threaded into each via their
// `viewSwitcher` prop and stays visible across views — switching back is
// always one click away. The selection is component-local React state, so it
// resets on reload (ephemeral, as designed).

import { useState } from 'react'

import type { EmbedConfig, PageInput } from './types'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { embedModeCaps, type NormalizedEmbedMode } from '@twasta/page-schema'
import { useDocumentInterface } from '@/livepreview/runtime/useDocumentInterface'
import {
  InterfaceSwitcher, EMBED_INTERFACE_VIEWS, type EmbedListView,
} from '@twasta/page-runtime/txn/list'
import { isMobileViewport } from '@twasta/page-runtime/txn/compact'

import EmbedLivePreview, { type FilterSelection } from './EmbedLivePreview'
import EmbedCaptureTabularPreview from './EmbedCaptureTabularPreview'
import EmbedCapturePreview from './EmbedCapturePreview'

// The switcher control + view union now live in the shared package; keep
// re-exporting them here so existing importers (LivePreviewTab) still work.
export { InterfaceSwitcher } from '@twasta/page-runtime/txn/list'
export type { EmbedListView } from '@twasta/page-runtime/txn/list'

interface Props {
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  onSetInput?: (name: string, value: unknown) => void
  onSaved?: () => void
  dataAccess?: DataAccess
  // When true, the "View" switcher dropdown is rendered into the active
  // interface's toolbar so the viewer can swap interfaces. When false the
  // host still renders the saved interface, but with no switcher and no
  // switching — used on the design canvas and when the designer disabled
  // interface switching. Resolved by EmbedPreview (surface + embed flag).
  showSwitcher?: boolean
  // Canonical access mode — decides which affordances (add / delete / browse)
  // the underlying interfaces expose. Defaults to 'full' (all on) so author /
  // in-app preview surfaces behave as before.
  accessMode?: NormalizedEmbedMode
  // The block's explicit height (CSS length) from the Size & Position panel.
  // When set, the list fills it (scrolls inside); when absent the list adjusts
  // to its data up to a screen-height cap, then scrolls.
  bodyHeight?: string
}

// The view the switcher starts on is derived from the saved embed config so
// the host renders the author's chosen interface first, then lets the viewer
// swap. Capture-mode multi-tabular / multi-form-input map onto the matching
// runtime view; everything else falls back to the saved-records list.
function initialViewFor(embed: EmbedConfig): EmbedListView {
  const renderMode = embed.renderMode || 'realtime'
  if (renderMode === 'capture' || renderMode === 'integrate') {
    if (embed.captureTemplate === 'multi-tabular') return 'tabular'
    if (embed.captureTemplate === 'multi-form-input') return 'form-input'
    if (embed.captureTemplate === 'multi-compact') return 'compact'
  }
  return 'list'
}

export default function EmbedListInterfaceHost({
  project, embed, inputs, inputValues, onSetInput, onSaved, dataAccess,
  showSwitcher = false, accessMode = 'full', bodyHeight,
}: Props) {
  // Auto-mobile: on a phone-width viewport where switching is offered (runtime,
  // not the design canvas), default to the compact interface. The user can
  // still switch back. The canvas (showSwitcher=false) never auto-switches.
  const [view, setView] = useState<EmbedListView>(
    () => (showSwitcher && isMobileViewport()) ? 'compact' : initialViewFor(embed),
  )
  // Mode-driven affordance gating (server-enforced too). edit → no add/delete
  // of header records; multi_add → add allowed but no browse chrome, etc.
  const caps = embedModeCaps(accessMode)

  // Active enforced-filter selection, lifted here so switching between the
  // List, multi-tabular, and form-input views keeps the SAME filter set /
  // overrides instead of the tabular view loading the full table. The List
  // view (EmbedLivePreview) owns the filter UI and reports changes up.
  const [filterSel, setFilterSel] = useState<FilterSelection>({ bypass: false, overrides: {} })

  // Only mount the dropdown when switching is allowed; otherwise the host
  // renders the saved interface with no switcher (canvas / disabled). The
  // allowlist is explicit: this host implements exactly the four embed
  // interfaces below, so it must not offer the transaction screen's 'simple'
  // record-editor layout (which would fall through to form-input here).
  // "Document" — offered only where a Document Form is anchored on this
  // transaction, the same conditional shape TxnScreen uses. A switcher must
  // never offer a view its own host cannot render, so the option and the
  // renderer arrive together or not at all.
  const { documentView } = useDocumentInterface({
    project, objName: embed.obj_name, canEdit: caps.headerEdit, onSaved,
  })
  const viewOptions = documentView
    ? [...EMBED_INTERFACE_VIEWS, 'document' as EmbedListView]
    : EMBED_INTERFACE_VIEWS

  const switcher = showSwitcher
    ? <InterfaceSwitcher value={view} options={viewOptions} onChange={setView} />
    : undefined

  if (view === 'document' && documentView) {
    /*
     * The switcher sits ABOVE the document, as the embed's own chrome — never
     * inside it. Choosing how an object is shown belongs to the object's
     * surface; the document is the editor for one record, and an editor that
     * offers to change the screen it lives in has to answer what becomes of a
     * half-typed document when somebody does.
     */
    return (
      <div style={{
        height: bodyHeight || '70vh', minHeight: 320,
        display: 'flex', flexDirection: 'column',
      }}>
        {switcher && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', padding: '4px 6px',
          }}>{switcher}</div>
        )}
        <div style={{ flex: 1, minHeight: 0 }}>
          {documentView({ canEdit: caps.headerEdit })}
        </div>
      </div>
    )
  }

  if (view === 'tabular') {
    return (
      <EmbedCaptureTabularPreview
        project={project}
        enforcedOverrides={filterSel.overrides}
        filterSetId={filterSel.filterSetId}
        bypassEnforced={filterSel.bypass}
        onApplyOverrides={(ov) => setFilterSel((s) => ({ ...s, overrides: ov }))}
        onSelectFilterSet={(sel) => setFilterSel({
          filterSetId: sel.bypass ? undefined : sel.setId,
          bypass: !!sel.bypass,
          overrides: {},
        })}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        viewSwitcher={switcher}
        canAdd={caps.headerAdd}
        canEdit={caps.headerEdit}
      />
    )
  }

  if (view === 'form-input') {
    return (
      <EmbedCapturePreview
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
        viewSwitcher={switcher}
        canAdd={caps.headerAdd}
        canEdit={caps.headerEdit}
      />
    )
  }

  if (view === 'compact') {
    // The compact interface reuses the capture list+form component in its
    // mobile-style card variant. It needs a capture/integrate render mode + the
    // multi-compact template so the underlying data path (retrieve + buffered
    // batch save) runs, even when the viewer switched to it from a realtime
    // list embed.
    return (
      <EmbedCapturePreview
        variant="compact"
        project={project}
        embed={{
          ...embed,
          renderMode: embed.renderMode === 'integrate' ? 'integrate' : 'capture',
          captureTemplate: 'multi-compact',
        }}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
        viewSwitcher={switcher}
        canAdd={caps.headerAdd}
        canEdit={caps.headerEdit}
      />
    )
  }

  // Default: the saved-records list.
  return (
    <EmbedLivePreview
      project={project}
      embed={embed}
      inputs={inputs}
      inputValues={inputValues}
      dataAccess={dataAccess}
      onSetInput={onSetInput}
      viewSwitcher={switcher}
      onFilterSelChange={setFilterSel}
      canAdd={caps.headerAdd}
      canDelete={caps.headerDelete}
      bodyHeight={bodyHeight}
    />
  )
}
